import { useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';

import type {
	AiStreamingOrchestratorPlugin,
	StreamingCommand,
	StreamingError,
} from './aiStreamingOrchestratorPluginType';
import { createStreamingCommands } from './editor-actions';
import { StreamCommandQueue } from './editor-actions/streamCommandQueue';
import { isAStreamableCommand } from './editor-actions/utils/is-a-streamable-command';

export const aiStreamingOrchestratorPlugin: AiStreamingOrchestratorPlugin = ({ api, config }) => {
	if (!api) {
		throw new Error('api is not defined');
	}

	const skipStreaming = fg('platform_editor_ai_skip_free_gen_streaming');
	const actionCache = new Map<string, { adfStreamer: ADFStreamer; command: StreamingCommand }>();

	// Initialize the queue manager
	const streamQueue = skipStreaming ? undefined : new StreamCommandQueue(api);

	return {
		name: 'aiStreamingOrchestrator',
		actions: {
			initAction: (command) => {
				const actionId = crypto.randomUUID();
				if (skipStreaming) {
					const schema = api.core.sharedState.currentState()?.schema;
					if (!schema) {
						throw new Error('Schema is not defined');
					}
					actionCache.set(actionId, {
						command,
						adfStreamer: new ADFStreamer(schema),
					});
				} else {
					streamQueue?.initStreamCommand(command);
				}

				return actionId;
			},

			streamValue: (actionId: string, chunk: string) => {
				if (skipStreaming) {
					const action = actionCache.get(actionId);
					if (!action) {
						throw new Error('Action is not defined');
					}
					action.adfStreamer.processChunk(chunk);
				} else {
					// Use the queue manager to handle streaming
					// This will either process immediately if actor is ready, or queue for later
					streamQueue?.queueStreamCommand(actionId, chunk);
				}
			},

			finishAction: (actionId: string, error?: StreamingError) => {
				if (skipStreaming) {
					const action = actionCache.get(actionId);
					if (!action) {
						throw new Error('Action is not defined');
					}

					const { command, adfStreamer } = action;
					const adfNode = adfStreamer.getLastDoc();
					const schema = api.core.sharedState.currentState()?.schema;
					if (!schema) {
						throw new Error('Schema is not defined');
					}

					const value = schema.nodeFromJSON(adfNode);
					if (!value) {
						throw new Error('Invalid ADF node');
					}

					createStreamingCommands(api)[command.type]({
						...command,
						...(isAStreamableCommand(command) ? { value } : {}),
					});
					actionCache.delete(actionId);
				} else {
					const { send } = api.aiExperience.actions.getAiExperienceActor();
					if (error) {
						send({
							type: 'error',
							errorInfo: {
								failureReason: error.failureReason,
								statusCode: error.statusCode,
							},
						});
					} else {
						streamQueue?.endStreamCommand();
					}
				}
			},
		},
		usePluginHook: () => {
			useEffect(() => {
				return () => {
					streamQueue?.destroy();
				};
			}, []);
		},
	};
};
